// Shared value-slot policy — resolved-target value emission (T18a, P2-16).
// For positions where the TARGET column is positively known (a typed INSERT
// VALUES element — T18b; not a slot role, G23: the position keeps its clause
// classification) the resolver calls this instead of the scope-driven
// `emitValueSlotCandidates`. Columns are structurally illegal here
// (`legalSources.columns: false` is part of the type), so the emission is:
// enum values (resolver-owned callback, tier 1), the engine-legal keyword
// literals the resolver passes (NULL / DEFAULT per D13), a scalar-subquery
// snippet, and compat-tiered scalar routines. Shares compat/tiers/keywords
// internals with `emitValueSlotCandidates` (D10).

import type { ColumnMeta, CompletionCandidate, DatabaseEngineId } from '@/core/engines';
import type { OperandLiteralSqlType } from '@/features/sql-resolver/types/queryStructure';
import { isDeadRoutineReturn } from './deadReturns';
import { DEFAULT_ENGINES, pushScalarSubquerySnippet } from './keywords';
import { ENUM_VALUE_TIER, KEYWORD_TIER, routineCandidateTier } from './tiers';
import type { ValueSlotEngineAdapter, ValueSlotRoutineDescriptor } from './types';

export type ResolvedValueTarget = {
  readonly kind: 'insert-column';
  readonly column: ColumnMeta;
  readonly coarseType: OperandLiteralSqlType;
  /** Structural legality of candidate sources at this target. `columns` is
   *  typed `false` — an INSERT VALUES element cannot reference columns —
   *  and `keywords` carries the engine-legal literals the RESOLVER chose
   *  (NULL only when the column is nullable, DEFAULT per engine — D13). */
  readonly legalSources: { readonly columns: false; readonly keywords: readonly string[] };
  /** Resolver-owned enum-value discovery (native enum members, MetaInfo
   *  declarations, …). The policy stamps `ENUM_VALUE_TIER` — invoked ONLY
   *  here, so the G9 enum-collector ordering stays untouched. */
  readonly collectEnumValues?: () => readonly CompletionCandidate[];
};

/** The subset of the engine adapter this emission needs — T18b call sites
 *  sit outside the scalar-value branch and need not build the full scoped
 *  column closure. */
export type ResolvedValueAdapter = Pick<ValueSlotEngineAdapter, 'engine'> & {
  readonly collectScalarRoutines?: () => readonly ValueSlotRoutineDescriptor[];
};

/** The D13 keyword literals for a typed INSERT VALUES element: NULL when the
 *  target column is nullable, DEFAULT on engines whose VALUES list accepts
 *  it. Resolvers (T18b) build `legalSources.keywords` with this so the five
 *  clones cannot drift from the table in `keywords.ts`. */
export function legalValueKeywordsFor(
  engine: DatabaseEngineId,
  column: ColumnMeta,
): readonly string[] {
  const kws: string[] = [];
  if (column.nullable) kws.push('NULL');
  if (DEFAULT_ENGINES.has(engine)) kws.push('DEFAULT');
  return kws;
}

export function emitResolvedValueCandidates(
  out: CompletionCandidate[],
  target: ResolvedValueTarget,
  adapter: ResolvedValueAdapter,
): void {
  // Enum values first — tier is policy-owned regardless of what the
  // resolver's callback stamped.
  if (target.collectEnumValues) {
    for (const c of target.collectEnumValues()) {
      out.push({ ...c, tier: ENUM_VALUE_TIER });
    }
  }

  // Engine-legal keyword literals (NULL / DEFAULT — resolver-chosen, D13).
  for (const kw of target.legalSources.keywords) {
    out.push({
      label: kw,
      insertText: kw,
      isSnippet: false,
      kind: 'keyword',
      tier: KEYWORD_TIER,
      detail: 'SQL keyword',
    });
  }

  // `(SELECT …)` — a scalar subquery is a legal VALUES element everywhere.
  pushScalarSubquerySnippet(out);

  // Scalar routines, compat-tiered against the TARGET column's type (the
  // target is always resolved — `lhsResolved` true by construction). Dead
  // returns dropped (spec §A2), underscore internals demoted (spec §A3).
  for (const r of adapter.collectScalarRoutines?.() ?? []) {
    if (isDeadRoutineReturn(r.returnsRaw)) continue;
    out.push({
      label: r.label,
      insertText: r.insertText,
      isSnippet: r.insertText.includes('$'),
      kind: 'routine',
      tier: routineCandidateTier(adapter.engine, target.coarseType, true, r),
      detail: `returns ${r.returnsRaw ?? r.returnsCoarse}`,
      triggerSignatureHelp: true,
    });
  }
}
